import { IconAtom2Outline18 as NoAgentIcon } from "nucleo-ui-outline-18"

import { Avatar, Badge } from "@productlane/ui"

import type { DataOptionType } from "@/components/popover-button"

interface Person {
	id: string
	name: string
	avatarUrl?: string | null
	email?: string
}

// Agents and members share one picker, so an agent's value carries a prefix.
const AGENT_PREFIX = "agent:"
const NO_AGENT_VALUE = AGENT_PREFIX
export const AGENT_MENU_VALUE = "agent-menu"

export const agentValue = (id: string) => `${AGENT_PREFIX}${id}`

const personLabel = ({ name, avatarUrl }: Person) => (
	<div className="flex min-w-0 items-center gap-2">
		<Avatar name={name} image={avatarUrl} size="xxs" />
		<span className="truncate">{name}</span>
	</div>
)

/** Linear's delegated chip: the assignee's avatar with the agent's tucked behind it. */
export const OwnerAvatars = ({
	assignee,
	delegate,
	size = "xs",
}: {
	assignee: Pick<Person, "name" | "avatarUrl">
	delegate?: Pick<Person, "name" | "avatarUrl"> | null
	size?: "xxs" | "xs"
}) => (
	<div className="flex flex-none items-center">
		<Avatar name={assignee.name} image={assignee.avatarUrl} size={size} />
		{delegate && (
			<Avatar
				name={delegate.name}
				image={delegate.avatarUrl}
				size={size}
				className="-ml-1.5 ring-2 ring-white dark:ring-gray-900"
			/>
		)}
	</div>
)

const ownerLabel = (assignee: Person, delegate: Person) => (
	<div className="flex min-w-0 items-center gap-2">
		<OwnerAvatars assignee={assignee} delegate={delegate} />
		<span className="truncate">{assignee.name}</span>
	</div>
)

export function findSelf<T extends Pick<Person, "email">>(
	members: readonly T[] | null | undefined,
	email: string | null | undefined,
) {
	const normalized = email?.trim().toLowerCase()
	if (!normalized) return undefined
	return members?.find((member) => member.email?.toLowerCase() === normalized)
}

/**
 * Linear's assignee menu: you first, then agents, then the team. Once an agent is
 * delegated it sits under the assignee, and its row opens the agent choices.
 */
export function assigneeAndAgentOptions({
	members,
	agents,
	selfEmail,
	assigneeId,
	delegateId,
}: {
	members: readonly Person[] | null | undefined
	agents: readonly Person[] | null | undefined
	selfEmail?: string | null
	assigneeId?: string | null
	delegateId?: string | null
}): DataOptionType[] {
	const self = findSelf(members, selfEmail)
	const delegate = delegateId
		? agents?.find((agent) => agent.id === delegateId)
		: undefined
	const delegatedRow = (group?: string): DataOptionType[] =>
		delegate
			? [
					{
						value: AGENT_MENU_VALUE,
						group,
						triggerLabel: personLabel(delegate),
						label: (
							<div className="flex min-w-0 items-center gap-2">
								{/* The curve runs up into the assignee row, as in Linear's menu. */}
								<span className="relative h-4 w-4 flex-none">
									<span className="absolute bottom-1/2 left-2 h-[22px] w-2.5 rounded-bl-md border-b border-l border-gray-300 dark:border-gray-600" />
								</span>
								{personLabel(delegate)}
								<Badge
									variant="important"
									size="sm"
									className="flex-none text-xs font-normal"
								>
									Agent
								</Badge>
							</div>
						),
						submenu: [
							{
								value: NO_AGENT_VALUE,
								label: (
									<div className="flex items-center gap-2">
										<NoAgentIcon className="h-4 w-4 flex-none text-secondary" />
										<span>No agent</span>
									</div>
								),
							},
							...(agents ?? []).map((agent) => ({
								value: agentValue(agent.id),
								label: personLabel(agent),
							})),
						],
					},
				]
			: []
	const assigneeIsListed =
		!!assigneeId && members?.some((member) => member.id === assigneeId)

	return [
		...(self
			? [
					{
						value: self.id,
						label: personLabel(self),
						triggerLabel:
							delegate && self.id === assigneeId
								? ownerLabel(self, delegate)
								: undefined,
					},
				]
			: []),
		...(self && self.id === assigneeId ? delegatedRow() : []),
		...(!assigneeIsListed ? delegatedRow() : []),
		...(delegate
			? []
			: (agents ?? []).map((agent) => ({
					value: agentValue(agent.id),
					label: personLabel(agent),
					group: "Agents",
				}))),
		...(members ?? [])
			.filter((member) => member.id !== self?.id)
			.flatMap((member) => [
				{
					value: member.id,
					label: personLabel(member),
					group: "Team members",
					triggerLabel:
						delegate && member.id === assigneeId
							? ownerLabel(member, delegate)
							: undefined,
				},
				...(member.id === assigneeId ? delegatedRow("Team members") : []),
			]),
	]
}

interface AssigneeChoice {
	assigneeId?: string | null
	delegateId?: string | null
}

/**
 * Delegating keeps a person responsible, as in Linear: the agent works for the
 * assignee, or for you when nobody is assigned. "No assignee" clears both.
 */
export function parseAssigneeChoice(
	value: string | null,
	{ assigneeId, selfId }: { assigneeId?: string | null; selfId?: string },
): AssigneeChoice {
	if (value === null) return { assigneeId: null, delegateId: null }
	if (value === NO_AGENT_VALUE) return { delegateId: null }
	if (value.startsWith(AGENT_PREFIX)) {
		const delegateId = value.slice(AGENT_PREFIX.length)
		return !assigneeId && selfId
			? { delegateId, assigneeId: selfId }
			: { delegateId }
	}
	return { assigneeId: value }
}
